import { useCallback, useEffect, useMemo, useState } from 'react'
import type { WeekRow } from '@/lib/types'
import {
  emptyRow,
  localKeysOnDay,
  mergeRows,
  nextSlot,
  rowKeyFor,
  slotOnDay,
  type ExtraRow,
  type NewRowSelection,
} from './week-rows'

export interface TimesheetRows {
  /** Serverregels plus de lokaal toegevoegde, in de juiste volgorde. */
  rows: WeekRow[]
  /**
   * Regels die deze sessie op deze dag zijn toegevoegd en nog niets opgeslagen hebben.
   * De dagweergave toont ze naast de regels die op die dag een boeking hebben.
   */
  localKeysForDay: (dayIndex: number) => Set<string>
  /** Regels waarvoor al invoer is weggeschreven; niet langer lokaal, ook voor het serverantwoord binnen is. */
  savedRowKeys: Set<string>
  /** Regel waarvan de invoer net is toegevoegd; laat de landing zien en stuurt de focus. */
  landedRowKey: string | null
  /**
   * Zet een lege regel op deze bestemming neer; een tweede activiteit is dezelfde keuze
   * opnieuw. `dayIndex` is de dag waarop de regel hoort, of null vanuit het weekrooster.
   */
  addRow: (selection: NewRowSelection, dayIndex: number | null) => string
  /** Haalt een nog niet opgeslagen regel weer weg. */
  dropRow: (rowKey: string) => void
  /** Vervangt een nog niet opgeslagen regel door een andere keuze (project of categorie). */
  replaceRow: (rowKey: string, selection: NewRowSelection, dayIndex: number | null) => string
  /**
   * Zet een nog niet opgeslagen regel op het slot waar de server de boeking van deze dag
   * neerzet, en geeft de sleutel terug waaronder de regel verdergaat. Bij het toevoegen
   * kent de regel de dag nog niet en raadt hij zijn slot uit de hele week; bij het opslaan
   * is de dag wel bekend en kan dat exact. Zonder die correctie landt de boeking op een
   * andere regel dan die waarin je typte, en staat ze twee keer in beeld: een keer als
   * serverregel en een keer als de regel die op zijn eigen slot bleef wachten.
   */
  alignRow: (rowKey: string, dayIndex: number) => string
  /** Meldt dat er invoer voor deze regel is weggeschreven, nog voor het serverantwoord terug is. */
  markRowSaved: (rowKey: string) => void
  clearLanded: () => void
}

/**
 * Houdt de lokaal toegevoegde regels vast en voegt ze samen met de serverweek. Een keer
 * geinstantieerd op paginaniveau, zodat een net toegevoegde regel het wisselen tussen dag-
 * en weekweergave overleeft.
 */
export function useTimesheetRows(weekId: string, serverRows: WeekRow[], personId?: string): TimesheetRows {
  const [extraRows, setExtraRows] = useState<ExtraRow[]>([])
  const [landedRowKey, setLandedRowKey] = useState<string | null>(null)
  const [savedRowKeys, setSavedRowKeys] = useState<Set<string>>(() => new Set())

  // Lokale regels horen bij een week van een persoon: bij het wisselen van week of
  // persoon zijn ze niet langer van toepassing. Zonder deze reset dook een lokaal
  // toegevoegde regel op in elke volgende week, en op de teampagina zelfs in het
  // grid van een andere persoon.
  const scope = `${weekId}|${personId ?? ''}`
  const [scopeSeen, setScopeSeen] = useState(scope)
  if (scope !== scopeSeen) {
    setScopeSeen(scope)
    setExtraRows([])
    setLandedRowKey(null)
    setSavedRowKeys(new Set())
  }

  // Een regel die de server kent is geen lokale regel meer. Zonder deze opruiming bleef
  // hij in extraRows staan, en dook hij bij het verwijderen van de serverregel opnieuw op
  // als lege 0:00-regel: uit het weekrooster toegevoegd zelfs op alle dagen van de week,
  // want zo'n regel heeft geen dag om hem aan te binden.
  useEffect(() => {
    setExtraRows((prev) => {
      const serverKeys = new Set(serverRows.map((r) => r.rowKey))
      const kept = prev.filter((r) => !serverKeys.has(rowKeyFor(r, r.slot)))
      return kept.length === prev.length ? prev : kept
    })
  }, [serverRows])

  const rows = useMemo(() => {
    const serverKeys = new Set(serverRows.map((r) => r.rowKey))
    return mergeRows(
      serverRows,
      extraRows.filter((r) => !serverKeys.has(rowKeyFor(r, r.slot))).map((r) => emptyRow(r, weekId)),
    )
  }, [serverRows, extraRows, weekId])

  const localKeysForDay = useCallback(
    (dayIndex: number) => localKeysOnDay(extraRows, serverRows, dayIndex),
    [extraRows, serverRows],
  )

  const append = useCallback((selection: NewRowSelection, slot: number, dayIndex: number | null): string => {
    const rowKey = rowKeyFor(selection, slot)
    setExtraRows((prev) => [...prev, { ...selection, slot, dayIndex }])
    setLandedRowKey(rowKey)
    return rowKey
  }, [])

  const addRow = useCallback(
    (selection: NewRowSelection, dayIndex: number | null) =>
      append(selection, nextSlot(rows, selection.projectId, selection.categoryId), dayIndex),
    [append, rows],
  )

  const dropRow = useCallback((rowKey: string) => {
    setExtraRows((prev) => prev.filter((r) => rowKeyFor(r, r.slot) !== rowKey))
    setLandedRowKey((current) => (current === rowKey ? null : current))
  }, [])

  const replaceRow = useCallback(
    (rowKey: string, selection: NewRowSelection, dayIndex: number | null): string => {
      // Slot bepalen zonder de te vervangen regel; een bestaande servercombinatie
      // krijgt zo vanzelf een volgend slot en overleeft de merge-dedup.
      const slot = nextSlot(rows.filter((r) => r.rowKey !== rowKey), selection.projectId, selection.categoryId)
      const newKey = rowKeyFor(selection, slot)
      setExtraRows((prev) => [
        ...prev.filter((r) => rowKeyFor(r, r.slot) !== rowKey),
        { ...selection, slot, dayIndex },
      ])
      // De regel verspringt naar het blok van zijn nieuwe klant; de landing wijst hem aan.
      setLandedRowKey(newKey)
      return newKey
    },
    [rows],
  )

  const alignRow = useCallback(
    (rowKey: string, dayIndex: number): string => {
      const extra = extraRows.find((r) => rowKeyFor(r, r.slot) === rowKey)
      if (extra === undefined) return rowKey
      const slot = slotOnDay(serverRows, extra.projectId, extra.categoryId, dayIndex)
      const newKey = rowKeyFor(extra, slot)
      // Een tweede regel die op dezelfde dag naar hetzelfde slot wijst laat zijn gok staan;
      // twee regels op een sleutel zou erger zijn dan de gok zelf.
      if (newKey === rowKey || extraRows.some((r) => rowKeyFor(r, r.slot) === newKey)) return rowKey
      setExtraRows((prev) => prev.map((r) => (rowKeyFor(r, r.slot) === rowKey ? { ...r, slot } : r)))
      setLandedRowKey((current) => (current === rowKey ? newKey : current))
      return newKey
    },
    [extraRows, serverRows],
  )

  const markRowSaved = useCallback((rowKey: string) => {
    setSavedRowKeys((prev) => (prev.has(rowKey) ? prev : new Set(prev).add(rowKey)))
  }, [])

  const clearLanded = useCallback(() => setLandedRowKey(null), [])

  return {
    rows,
    localKeysForDay,
    savedRowKeys,
    landedRowKey,
    addRow,
    dropRow,
    replaceRow,
    alignRow,
    markRowSaved,
    clearLanded,
  }
}
